import { useState, useEffect, useCallback } from 'react';
import { authAPI, QuickSignupData } from '../services/apiService';

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  sex: string;
  username: string;
  onboardingCompleted: boolean;
  onboardingStep: number;
  profileCompleteness: number;
  role?: 'user' | 'admin'; // User role (defaults to 'user')
  // Add these to prevent type errors when backend returns them:
  profileCompleted?: boolean;
  canViewProfiles?: boolean;
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);

  useEffect(() => {
    // Check for existing session
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('userData');
    
    if (savedToken && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setToken(savedToken);
        setUser(parsedUser);
      } catch (error) {
        console.error('Failed to parse saved user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
      }
    }
    setLoading(false);
  }, []);

  const quickSignup = async (data: QuickSignupData): Promise<{ success: boolean; error?: string }> => {
    setSignupLoading(true);
    try {
      const response = await authAPI.quickSignup(data);
      
      if (response.success && response.data) {
        setToken(response.data.token);
        setUser(response.data.user);
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('userData', JSON.stringify(response.data.user));
        return { success: true };
      }
      
      return { success: false, error: response.message || 'Signup failed' };
    } catch (error) {
      return { success: false, error: (error as Error).message };
    } finally {
      setSignupLoading(false);
    }
  };

  const login = async (usernameOrEmail: string, password: string): Promise<{ success: boolean; error?: string }> => {
    setLoginLoading(true);
    try {
      const response = await authAPI.login(usernameOrEmail, password);
      
      if (response.success && response.data) {
        setToken(response.data.token);
        setUser(response.data.user);
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('userData', JSON.stringify(response.data.user));
        return { success: true };
      }
      
      return { success: false, error: response.message || 'Login failed' };
    } catch (error) {
      return { success: false, error: (error as Error).message };
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
  };

  const updateUser = (updates: Partial<User>) => {
    console.log('updateUser called with:', updates);
    console.log('Current user before update:', user);

    if (user) {
      const updatedUser = { ...user, ...updates };

      console.log('Updated user object:', updatedUser);
      setUser(updatedUser);
      localStorage.setItem('userData', JSON.stringify(updatedUser));
      console.log('User state should be updated');
    }
  };

  // Smart redirect helper
  const getRedirectPath = useCallback((user: User | null): string => {
    if (!user) return '/login';
    
    // Admin users redirect to admin dashboard
    if (user.role === 'admin') {
      return '/admin/dashboard';
    }
    
    // If user hasn't completed onboarding, redirect to onboarding
    if (!user.onboardingCompleted) {
      return '/onboarding';
    }
    
    // If profile is incomplete, redirect to dashboard to complete it
    if (user.profileCompleteness < 100) {
      return '/dashboard';
    }
    
    // Fully set up user, redirect to dashboard
    return '/dashboard';
  }, []);

  // Check if user needs onboarding
  const needsOnboarding = useCallback((user: User | null): boolean => {
    return user ? !user.onboardingCompleted : false;
  }, []);

  // Check if user profile is complete
  const isProfileComplete = useCallback((user: User | null): boolean => {
    return user ? user.profileCompleteness >= 100 : false;
  }, []);

  return {
    user,
    token,
    loading,
    loginLoading,
    signupLoading,
    isAuthenticated: !!user,
    quickSignup,
    login,
    logout,
    updateUser,
    getRedirectPath,
    needsOnboarding,
    isProfileComplete
  };
};
